import { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ReactErrorBoundaryProps {
  children: ReactNode;
  onError?: (error: Error) => void;
}

// React Error Boundary to catch component rendering errors
export class ReactErrorBoundary extends Component<ReactErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ReactErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    console.error('[ReactErrorBoundary] Error caught by boundary:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ReactErrorBoundary] Component error details:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });

    // Track error in analytics
    import('../services/analytics').then(({ analytics }) => {
      analytics.trackError(error.message, 'react_component_error');
    });

    if (this.props.onError) {
      this.props.onError(error);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 bg-red-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl border border-red-200 max-w-md">
            <h2 className="text-xl font-semibold text-red-800 mb-4">React Component Error</h2>
            <p className="text-red-600 mb-4">
              A React component failed to render properly. This might be due to initialization
              issues.
            </p>
            <details className="mb-4">
              <summary className="cursor-pointer text-red-700 font-medium">Error Details</summary>
              <pre className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded overflow-auto max-h-32">
                {this.state.error?.message}
                {'\n'}
                {this.state.error?.stack}
              </pre>
            </details>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ReactErrorBoundary;
